/**
 * Handoff Badge Styles
 * Centralized styling for handoff hotness levels and statuses
 */

export interface StatusStyleResult {
  bg: string;
  text: string;
  border: string;
  label: string;
}

// Status badge styles.
//
// SPEC-2300 collapsed the lane to one 72-hour `pending` window, so `hold`,
// `call_in_progress`, and the soft-expiry `expired` bucket no longer occur;
// `returned_to_remi` is the legacy name for `released` and is normalized to it
// on read (see normalizeHandoffStatus in api/handoff.ts).
export const getStatusStyle = (status: string): StatusStyleResult => {
  // Soft translucent pill in both modes — same vibe as dark mode but lighter
  const styles: Record<string, StatusStyleResult> = {
    pending: {
      bg: 'bg-green-500/15 dark:bg-green-500/25',
      text: 'text-green-700 dark:text-green-200',
      border: 'border-green-500/30 dark:border-green-500/40',
      label: 'Available to Claim',
    },
    claimed: {
      bg: 'bg-green-500/15 dark:bg-green-500/25',
      text: 'text-green-700 dark:text-green-200',
      border: 'border-green-500/30 dark:border-green-500/40',
      label: 'Claimed',
    },
    released: {
      bg: 'bg-purple-500/15 dark:bg-purple-500/25',
      text: 'text-purple-700 dark:text-purple-200',
      border: 'border-purple-500/30 dark:border-purple-500/40',
      label: 'Returned to Remi',
    },
    declined: {
      bg: 'bg-red-500/15 dark:bg-red-500/25',
      text: 'text-red-700 dark:text-red-200',
      border: 'border-red-500/30 dark:border-red-500/40',
      label: 'Declined',
    },
    expired: {
      bg: 'bg-amber-500/15 dark:bg-amber-500/25',
      text: 'text-amber-700 dark:text-amber-200',
      border: 'border-amber-500/30 dark:border-amber-500/40',
      label: 'Expired',
    },
    completed: {
      bg: 'bg-emerald-500/15 dark:bg-emerald-500/25',
      text: 'text-emerald-700 dark:text-emerald-200',
      border: 'border-emerald-500/30 dark:border-emerald-500/40',
      label: 'Converted',
    },
    cancelled: {
      bg: 'bg-gray-500/15 dark:bg-gray-500/25',
      text: 'text-gray-700 dark:text-gray-200',
      border: 'border-gray-500/30 dark:border-gray-500/40',
      label: 'Cancelled',
    },
  };

  return (
    styles[status] || {
      bg: 'bg-gray-500/15 dark:bg-gray-500/25',
      text: 'text-gray-700 dark:text-gray-200',
      border: 'border-gray-500/30 dark:border-gray-500/40',
      label: status,
    }
  );
};

// Email event type styles
export const getEmailEventStyle = (eventType: string): { bg: string; text: string } => {
  const styles: Record<string, { bg: string; text: string }> = {
    delivered: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
    opened: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
    clicked: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
    bounced: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
    dropped: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
    sent: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300' },
  };
  return styles[eventType.toLowerCase()] || styles.sent;
};
